import { useEffect, useRef, useState } from 'react';
import type { ChartResult } from '../domain/types';
import { createShareQrCode, createShareUrl, downloadTextFile, formatResultAsText, type ShareQrCode } from '../export/result';
import { Icon } from './Icon';

function safeJson(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    const json = JSON.stringify(value, (_key, item: unknown) => {
      if (typeof item === 'function') return undefined;
      if (item && typeof item === 'object') {
        if (seen.has(item)) return '[循环引用]';
        seen.add(item);
      }
      return item;
    }, 2);
    return json.length > 100_000 ? `${json.slice(0, 100_000)}\n…已省略过长数据` : json;
  } catch {
    return '原始数据无法序列化。';
  }
}

function ShareQrSvg({ code }: { code: ShareQrCode }) {
  const quietZone = 4;
  let path = '';
  for (let row = 0; row < code.size; row += 1) {
    for (let column = 0; column < code.size; column += 1) {
      if (code.data[row * code.size + column]) {
        path += `M${column + quietZone} ${row + quietZone}h1v1h-1z`;
      }
    }
  }
  const viewSize = code.size + quietZone * 2;
  return <svg className="share-qr-svg" role="img" aria-label="分享链接二维码" viewBox={`0 0 ${viewSize} ${viewSize}`} shapeRendering="crispEdges"><rect width={viewSize} height={viewSize} fill="#fff" /><path d={path} fill="#173d2d" /></svg>;
}

function isSharedResult(result: ChartResult): boolean {
  return typeof result.raw === 'object'
    && result.raw !== null
    && 'shared' in result.raw
    && result.raw.shared === true;
}

export function ChartResultView({ result }: { result: ChartResult }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareNotice, setShareNotice] = useState('');
  const [shareQr, setShareQr] = useState<ShareQrCode | null>(null);
  const [shareQrNotice, setShareQrNotice] = useState('');
  const sharedResult = isSharedResult(result);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!shareUrl) return undefined;
    let active = true;
    void createShareQrCode(shareUrl).then((code) => {
      if (!active) return;
      setShareQr(code);
      setShareQrNotice('二维码已生成，可用手机扫码打开。');
    }).catch((cause: unknown) => {
      if (!active) return;
      setShareQrNotice(cause instanceof Error && /overflow|too long|code length/i.test(cause.message) ? '链接内容太长，二维码装不下；请使用上面的链接。' : '二维码生成失败，链接仍可正常复制。');
    });
    return () => {
      active = false;
    };
  }, [shareUrl]);

  function filename(extension: string): string {
    const safeName = result.moduleName.replace(/[\\/:*?"<>|]/g, '-');
    return `易鉴-${safeName}-${result.generatedAt.slice(0, 10)}.${extension}`;
  }

  function exportText() {
    downloadTextFile(filename('txt'), formatResultAsText(result));
    setShareNotice('文字文件已开始下载。');
  }

  function exportPdf() {
    setShareNotice('已打开打印窗口，请选择“另存为 PDF”保存精美排版结果。');
    document.body.classList.add('print-result');
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      document.body.classList.remove('print-result');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(() => window.print(), 60);
    window.setTimeout(cleanup, 60_000);
  }

  async function createShareLink() {
    try {
      const url = createShareUrl(result);
      setShareQr(null);
      setShareQrNotice('二维码生成中…');
      setShareUrl(url);
      setShareNotice(navigator.clipboard ? '分享链接已生成，可复制后发送。' : '分享链接已生成，请手动复制。');
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(url);
          setShareNotice('分享链接已复制到剪贴板。');
        } catch {
          setShareNotice('分享链接已生成，请手动复制。');
        }
      }
    } catch (cause) {
      setShareNotice(cause instanceof Error ? cause.message : '分享链接生成失败，请改用文字或 PDF 导出。');
      setShareUrl('');
    }
  }

  return (
    <article className="chart-result">
      <header className="result-header">
        <div>
          <span className="eyebrow">排盘完成</span>
          <h2 ref={headingRef} tabIndex={-1}>{result.headline}</h2>
          <p>{new Intl.DateTimeFormat('zh-CN', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(result.generatedAt))}</p>
        </div>
        <span className="verified-badge"><Icon name={sharedResult ? 'book' : 'check'} size={16} />{sharedResult ? '别人分享的结果' : '本机排盘'}</span>
      </header>

      <div className="print-only print-brand" aria-hidden="true"><span className="print-brand-mark">易</span><div><strong>易鉴</strong><span>传统术数排盘结果</span></div></div>
      {result.question && <p className="result-question">所问：{result.question}</p>}
      <p className="print-only print-export-note">打印版包含盘面摘要、详细结果、规则出处和提醒。原始数据请使用“导出文字”查看。</p>

      <section className="result-actions" aria-label="结果导出与分享">
        <div className="result-action-buttons">
          <button type="button" className="secondary-button" onClick={exportText}><Icon name="book" size={16} />导出文字</button>
          <button type="button" className="secondary-button" onClick={exportPdf}><Icon name="arrow" size={16} />导出 PDF</button>
          <button type="button" className="secondary-button" onClick={() => void createShareLink()}><Icon name="spark" size={16} />生成分享链接</button>
        </div>
        {shareUrl && <div className="share-link-layout">
          <div className="share-qr-card">
            <div className="share-qr-frame">{shareQr ? <ShareQrSvg code={shareQr} /> : <span className="share-qr-placeholder">二维码准备中</span>}</div>
            <div><strong>手机扫码打开</strong><small role="status" aria-live="polite">{shareQrNotice}</small><small>二维码只在本机生成，不会上传内容。</small></div>
          </div>
          <div className="share-link-box">
            <label htmlFor="share-result-url">可分享网址</label>
            <div><input id="share-result-url" readOnly value={shareUrl} onFocus={(event) => event.currentTarget.select()} /><button type="button" className="text-button" onClick={() => void navigator.clipboard?.writeText(shareUrl)}>复制</button></div>
            <small>链接包含问题、盘面摘要和出处，不含 API Key、图片或原始数据。发送前请删掉隐私信息。</small>
          </div>
        </div>}
        {shareNotice && <p className="export-notice" role="status">{shareNotice}</p>}
      </section>

      <dl className="result-summary">
        {result.summary.map((item) => <div key={`${item.label}-${item.value}`}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
      </dl>

      {result.warnings.map((warning) => <p className="result-warning" key={warning}><Icon name="shield" size={18} />{warning}</p>)}

      <div className="result-sections">
        {result.sections.map((group) => (
          <section key={group.title}>
            <h3>{group.title}</h3>
            <dl>
              {group.facts.map((item) => <div key={`${group.title}-${item.label}`}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
            </dl>
          </section>
        ))}
      </div>

      <section className="evidence-panel" aria-labelledby="evidence-title">
        <div>
          <span className="evidence-icon"><Icon name="book" /></span>
          <div>
            <h3 id="evidence-title">规则出处</h3>
            <p>这里列出本次采用的文献和规则。不同流派可能有不同解释。</p>
          </div>
        </div>
        <ul>
          {result.evidence.map((item) => <li key={`${item.source}-${item.rule}`}><strong>{item.source}</strong><span>{item.rule}{item.note ? ` · ${item.note}` : ''}</span></li>)}
        </ul>
      </section>

      <details className="raw-details">
        <summary>查看原始数据（供核对）</summary>
        <pre>{safeJson(result.raw)}</pre>
      </details>
      <footer className="print-only print-footer" aria-hidden="true"><span>易鉴 · 传统术数研究工具</span><span>第 <b className="print-page-number" /> 页</span></footer>
    </article>
  );
}
